import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useState } from 'react';

const ENDPOINT_URL = '/shp-shipping/nearby-pickup-spots/{country_code}/{zip_code}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetAllNearbyPickupSpotsInput = paths[url]['get']['parameters']['path'];
export type GetAllNearbyPickupSpotsSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get nearby pickup spots.
 */
export const useGetAllNearbyPickupSpots = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setAllNearbyPickupSpots } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const [endpointInput, setEndpointInput] = useState<GetAllNearbyPickupSpotsInput | null>(null);

    const request = async (props: GetAllNearbyPickupSpotsInput) => {
        setLoading(true);
        setError(null);
        setEndpointInput({'country_code' : props.country_code, 'zip_code': props.zip_code});

        try {
            const inputParameters = new URLSearchParams({ ...getCommonParameters()}).toString();

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{country_code}', props.country_code || '').replace('{zip_code}', props.zip_code || '')}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.message || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetAllNearbyPickupSpotsSuccessResponse = await response.json();

            setAllNearbyPickupSpots(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime, endpointInput };
};
